import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { validateDisplayName } from "../auth/reviewer";
import { takeSignoutReason } from "../auth/signoutReason";
import { PAUSED_COPY } from "../components/PausedScreen";
import { useServicePaused } from "../paused";

// Pale notice on a dark page: the text colour has to be set, or it inherits
// the theme's near-white and disappears (seen on staging 2026-09-18).
const NOTICE_STYLE = {
  background: "#fff3cd", color: "#5c4500", padding: "0.75rem", borderRadius: 4,
} as const;

export function LoginPage(): JSX.Element {
  const auth = useAuth();
  const paused = useServicePaused();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [signoutReason, setSignoutReason] = useState<string | null>(null);

  // Read once, on arrival: the stash is cleared by the read, so the guard
  // keeps what we found when StrictMode runs this effect a second time.
  useEffect(() => {
    const reason = takeSignoutReason();
    if (reason !== null) setSignoutReason(reason);
  }, []);

  const displayNameError = useMemo(
    () => validateDisplayName(displayName, username.trim()),
    [displayName, username],
  );
  const formValid =
    username.trim().length > 0 && password.length > 0 && displayNameError === null;

  if (auth.status === "authed") return <Navigate to="/flights" replace />;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formValid) return;
    try {
      await auth.login({
        username: username.trim(),
        password,
        displayName: displayName.trim(),
      });
    } catch {
      // error rendered from auth.error
    }
  };

  const submitting = auth.status === "loading";

  return (
    <main style={{ fontFamily: "system-ui", maxWidth: 360, margin: "4rem auto" }}>
      <h1 style={{ marginBottom: 0 }}>Hawk Atlas</h1>
      <p style={{ marginTop: "0.25rem", color: "#888" }}>Sign in</p>
      {paused && (
        <p role="status" style={NOTICE_STYLE}>
          <strong>{PAUSED_COPY.title}.</strong> {PAUSED_COPY.body}
        </p>
      )}
      {signoutReason === "signed_in_elsewhere" && (
        <p role="status" style={NOTICE_STYLE}>
          You were signed out because someone signed in as you on another device. Anything you
          hadn't saved in the last few seconds is gone. If that wasn't you, use a different name
          and let Luke know.
        </p>
      )}
      <form onSubmit={onSubmit} noValidate>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          Team username
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label style={{ display: "block", marginBottom: "0.25rem" }}>
          Your name
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            required
            aria-invalid={displayNameError !== null}
            style={{ display: "block", width: "100%" }}
          />
        </label>
        {displayName.length > 0 && displayNameError !== null && (
          <p style={{ color: "#b00020", fontSize: 13, margin: "0 0 0.75rem 0" }}>
            {displayNameError.message}
          </p>
        )}
        {displayName.length === 0 && (
          <p style={{ color: "#666", fontSize: 13, margin: "0 0 0.75rem 0" }}>
            Required — used to attribute pins, POIs, and notes.
          </p>
        )}
        <button type="submit" disabled={submitting || !formValid}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        {auth.status === "error" && auth.error !== null && (
          <p role="alert" style={{ color: "#b00020" }}>
            {auth.error}
          </p>
        )}
      </form>
    </main>
  );
}
